import React, { useEffect, useState } from "react";
import { FaRankingStar } from "react-icons/fa6";
import { VscLocation } from "react-icons/vsc";
import { useParams, useNavigate } from "react-router-dom";
import { DeleteJob, GetJobDetails } from "Services/OtherApis";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";

const JobDetails = () => {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.dir() === "rtl";
  const navigate = useNavigate();
  const { id } = useParams();
  const [data, setData] = useState({});
  const [loading, setLoading] = useState(true);
  const [showDeletePopup, setShowDeletePopup] = useState(false); // State for popup visibility
  const [language, setSelectedLanguage] = useState(isRTL ? "ar" : "en");
  useEffect(() => {
    getJobDetails();
  }, []);

  async function getJobDetails() {
    setLoading(true);
    try {
      const res = await GetJobDetails(id);

      setData(res || {}); // Ensure data is always an object
    } catch (e) {
      console.error("Error fetching jobs:", e);
    } finally {
      setLoading(false);
    }
  }

  const handleDelete = async () => {
    try {
      const res = await DeleteJob(id);
      if (res?.status === 200) {
        toast.success("Job deleted successfully!");
        navigate(-1);
      }
    } catch (e) {
      toast.success("Error deleting job!");
      console.error("Error fetching jobs:", e);
    }
  };

  return (
    <div>
      {loading ? (
        <p className="text-gray-600 mt-4 text-center border border-gray-300 py-24 text-lg rounded-md">
          {t("Loading Job Details")} ...
        </p>
      ) : Object.keys(data).length === 0 ? (
        <p className="text-gray-600 mt-4 text-center border border-gray-300 py-24 text-lg rounded-md">
          {t("No data available for this job")} !
        </p>
      ) : (
        <>
          {data.jobTitle?.[language] !== "" ? (
            <div className="mx-auto p-6">
              <div className="flex items-center justify-between mb-6">
                <div className="flex flex-row  space-x-4 rtl:space-x-reverse">
                  <img src={data?.image} className="h-20" />
                  <div>
                    <h1 className="text-2xl font-bold">
                      {data.jobTitle?.[language]}
                    </h1>
                    <div className=" mt-3 flex items-center space-x-4 rtl:space-x-reverse text-sm text-gray-600">
                      <span className="bg-teal-500 text-white px-3 py-1 rounded-md text-xs font-semibold">
                        {data?.jobType?.[language]}
                      </span>
                      <span>•</span>
                      <span className="flex items-center space-x-2 rtl:space-x-reverse">
                        <FaRankingStar className="h-4 w-4 mr-1" />
                        <a> {data?.experience?.[language]}</a>
                      </span>
                      <span>•</span>
                      <span className="flex items-center space-x-2 rtl:space-x-reverse">
                        <VscLocation className="h-4 w-4 mr-1" />
                        <a> {data?.location?.[language]}</a>
                      </span>
                    </div>
                  </div>
                </div>

                <div className="flex flex-row space-x-4 rtl:space-x-reverse">
                  <button
                    className="px-4 py-2 bg-gray-400 hover:bg-gray-500 duration-300 text-white rounded-md text-sm"
                    onClick={() => navigate(-1)}
                  >
                    {t("Back")}
                  </button>
                  <button
                    className="px-4 py-2 bg-teal-500 hover:bg-teal-600 duration-300 text-white rounded-md text-sm"
                    onClick={() => navigate(`/career/update-job/${data?._id}`)}
                  >
                    {t("Edit")}
                  </button>
                  <button
                    className="px-4 py-2 bg-teal-500 text-white hover:bg-teal-600 duration-300 rounded-md text-sm"
                    onClick={() =>
                      navigate(`/applicants/allapplicants?job=${data?._id}`)
                    } // Show popup on delete button click
                  >
                    {t("Applicants")}
                  </button>
                </div>
              </div>

              <hr className="my-6" />

              <p className="text-gray-700 text-sm mb-6">
                {data?.description?.[language]}
              </p>

              <h2 className="text-lg font-semibold text-purple-600 mb-2">
                {t("Duties and Responsibilities")} :
              </h2>
              <ul className="list-disc list-inside text-gray-700 mb-6">
                {data?.duties?.[language]?.map((duty, index) => (
                  <li key={index}>{duty}</li>
                ))}
              </ul>

              <h2 className="text-lg font-semibold text-purple-600 mb-2">
                {t("Qualifications / Requirements")} :
              </h2>
              <ul className="list-disc list-inside text-gray-700 mb-6">
                {data?.qualifications?.[language].map(
                  (qualification, index) => (
                    <li key={index}>{qualification}</li>
                  )
                )}
              </ul>

              <h2 className="text-lg font-semibold text-purple-600 mb-2">
                {t("Required Documents")}:
              </h2>
              <p className="text-gray-700 mb-2">
                {t(
                  "Please submit the following documents in a single PDF file"
                )}{" "}
                :
              </p>
              <ul className="list-disc list-inside text-gray-700 mb-6">
                {data?.requiredDocuments?.[language].map((document, index) => (
                  <li key={index}>{document}</li>
                ))}
              </ul>

              <div className="mt-6">
                {" "}
                <button
                  className="px-4 py-2 bg-red-500 text-white hover:bg-red-600 duration-300 rounded-md text-sm"
                  onClick={() => setShowDeletePopup(true)} // Show popup on delete button click
                >
                  {t("Delete This Job")}
                </button>
              </div>
            </div>
          ) : (
            <p className="text-gray-600 mt-4 text-center border border-gray-300 py-24 text-lg rounded-md">
              {language === "ar"
                ? t("Job is not available in") + " " + t("Arabic")
                : t("Job is not available in") + " " + t("English")}
              !
            </p>
          )}
        </>
      )}

      {showDeletePopup && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white px-4 py-5 rounded-md shadow-lg text-center max-w-[500px]">
            <h2 className="text-lg  mb-4 text-gray-700">
              {t("Are you sure you want to delete the job")}
              <span className="text-gray-800 font-semibold">
                {" " + data?.jobTitle?.[language] + " "}
              </span>
              ?
            </h2>
            <div className="flex justify-center space-x-4 rtl:space-x-reverse">
              <button
                className="px-4 py-2 bg-gray-500 text-white rounded-md"
                onClick={() => setShowDeletePopup(false)} // Close popup
              >
                {t("Cancel")}
              </button>
              <button
                className="px-4 py-2 bg-red-500 text-white rounded-md"
                onClick={() => {
                  setShowDeletePopup(false); // Close popup
                  handleDelete(); // Call delete function
                }}
              >
                {t("Confirm")}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default JobDetails;
